import React, { useState } from 'react';
import { X } from 'lucide-react';
import {
  ComplianceRequirement,
  ComplianceStatus,
  MappingType,
  ControlStrength,
} from './types';

interface Props {
  requirement: ComplianceRequirement;
  onClose: () => void;
  onRecordAssessment: (data: {
    assessor_name: string;
    assessor_role: string;
    status_assigned: ComplianceStatus;
    why_compliant?: string;
    gap_analysis?: string;
    evidence_summary?: string;
    finding_reference?: string;
    capa_reference?: string;
  }) => void;
  onAddMapping: (data: {
    mapping_type: MappingType;
    target_reference_id: string;
    target_title: string;
    control_strength: ControlStrength;
    created_by: string;
  }) => void;
  onRemoveMapping: (mappingId: string) => void;
  onAddSourceLink: (canonicalId: string, notes?: string) => void;
}

export const ComplianceRequirementDetailModal: React.FC<Props> = ({
  requirement,
  onClose,
  onRecordAssessment,
  onAddMapping,
  onRemoveMapping,
  onAddSourceLink,
}) => {
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'ASSESSMENTS' | 'MAPPINGS' | 'SOURCES'>('OVERVIEW');

  // Assessment form state
  const [asmtStatus, setAsmtStatus] = useState<ComplianceStatus>('COMPLIANT');
  const [assessorName, setAssessorName] = useState('Safety Auditor');
  const [assessorRole, setAssessorRole] = useState('Compliance Monitoring Manager');
  const [whyCompliant, setWhyCompliant] = useState('');
  const [gapAnalysis, setGapAnalysis] = useState('');
  const [evidenceSummary, setEvidenceSummary] = useState('');
  const [findingRef, setFindingRef] = useState('');
  const [capaRef, setCapaRef] = useState('');

  // Mapping form state
  const [mapType, setMapType] = useState<MappingType>('SOP_DOCUMENT');
  const [targetId, setTargetId] = useState('');
  const [targetTitle, setTargetTitle] = useState('');
  const [controlStrength, setControlStrength] = useState<ControlStrength>('ADEQUATE');

  // Source Link state
  const [canonicalId, setCanonicalId] = useState('');
  const [sourceNotes, setSourceNotes] = useState('');

  const handleAssessmentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onRecordAssessment({
      assessor_name: assessorName,
      assessor_role: assessorRole,
      status_assigned: asmtStatus,
      why_compliant: whyCompliant,
      gap_analysis: gapAnalysis,
      evidence_summary: evidenceSummary,
      finding_reference: findingRef || undefined,
      capa_reference: capaRef || undefined,
    });
    setWhyCompliant('');
    setGapAnalysis('');
    setEvidenceSummary('');
    setFindingRef('');
    setCapaRef('');
  };

  const handleMappingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetId || !targetTitle) return;
    onAddMapping({
      mapping_type: mapType,
      target_reference_id: targetId,
      target_title: targetTitle,
      control_strength: controlStrength,
      created_by: assessorName,
    });
    setTargetId('');
    setTargetTitle('');
  };

  const handleSourceLinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canonicalId) return;
    onAddSourceLink(canonicalId, sourceNotes || undefined);
    setCanonicalId('');
    setSourceNotes('');
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: '#dbeafe', color: '#1e40af' }}>
                {requirement.req_code}
              </span>
              <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', background: '#f1f5f9', color: '#475569', fontWeight: 600 }}>
                {requirement.regulation_title || requirement.regulation_id}
              </span>
            </div>
            <h3>{requirement.title}</h3>
            <p>Compliance requirement analysis, audit history, and operational controls mapping</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color, #e2e8f0)', background: 'var(--bg-subtle, #f8fafc)', padding: '0 24px', gap: '8px' }}>
          {(['OVERVIEW', 'ASSESSMENTS', 'MAPPINGS', 'SOURCES'] as const).map((tab) => {
            const labels: Record<string, string> = {
              OVERVIEW: 'Overview',
              ASSESSMENTS: `Compliance Assessments (${requirement.assessment_count || 0})`,
              MAPPINGS: `Operational Mappings (${requirement.mappings?.length || 0})`,
              SOURCES: `Framework References (${requirement.source_links?.length || 0})`,
            };
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'var(--primary-color, #2563eb)' : 'var(--text-muted, #64748b)',
                  borderBottom: isActive ? '2px solid var(--primary-color, #2563eb)' : '2px solid transparent',
                  background: 'transparent',
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  cursor: 'pointer',
                }}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>

        {/* Content Body */}
        <div className="modal-body">
          {activeTab === 'OVERVIEW' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ background: 'var(--bg-subtle, #f8fafc)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)' }}>
                <h4 style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Regulatory Mandate Statement
                </h4>
                <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-primary, #0f172a)', lineHeight: 1.6, fontStyle: 'italic', margin: '8px 0 0 0' }}>
                  "{requirement.requirement_statement}"
                </p>
              </div>

              {requirement.internal_interpretation && (
                <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <h4 style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Internal Operational Interpretation
                  </h4>
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#1e3a8a', lineHeight: 1.6 }}>
                    {requirement.internal_interpretation}
                  </p>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                <div style={{ border: '1px solid var(--border-color, #e2e8f0)', padding: '12px', borderRadius: '8px', background: 'var(--card-bg, #fff)' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase' }}>Status</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>{requirement.compliance_status}</span>
                </div>
                <div style={{ border: '1px solid var(--border-color, #e2e8f0)', padding: '12px', borderRadius: '8px', background: 'var(--card-bg, #fff)' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase' }}>Risk Level</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>{requirement.risk_level}</span>
                </div>
                <div style={{ border: '1px solid var(--border-color, #e2e8f0)', padding: '12px', borderRadius: '8px', background: 'var(--card-bg, #fff)' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase' }}>Responsible Department</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>{requirement.responsible_department}</span>
                </div>
                <div style={{ border: '1px solid var(--border-color, #e2e8f0)', padding: '12px', borderRadius: '8px', background: 'var(--card-bg, #fff)' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase' }}>Control Coverage</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                    {requirement.is_covered ? 'Covered' : 'Unmapped'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ASSESSMENTS' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Form to submit assessment */}
              <div style={{ background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', padding: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>
                  Record Periodic Compliance Assessment
                </h4>
                <form onSubmit={handleAssessmentSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="form-grid-3">
                    <div className="form-field">
                      <label>Compliance Status</label>
                      <select
                        value={asmtStatus}
                        onChange={(e) => setAsmtStatus(e.target.value as any)}
                      >
                        <option value="COMPLIANT">COMPLIANT</option>
                        <option value="PARTIALLY_COMPLIANT">PARTIALLY_COMPLIANT</option>
                        <option value="NON_COMPLIANT">NON_COMPLIANT</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Assessor Name</label>
                      <input
                        type="text"
                        value={assessorName}
                        onChange={(e) => setAssessorName(e.target.value)}
                      />
                    </div>
                    <div className="form-field">
                      <label>Assessor Role</label>
                      <input
                        type="text"
                        value={assessorRole}
                        onChange={(e) => setAssessorRole(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label>Compliance Justification / Findings</label>
                    <textarea
                      rows={2}
                      value={whyCompliant}
                      onChange={(e) => setWhyCompliant(e.target.value)}
                      placeholder="Evidence and justification supporting compliance status..."
                    />
                  </div>

                  {asmtStatus !== 'COMPLIANT' && (
                    <div className="form-field">
                      <label style={{ color: '#991b1b' }}>Gap Analysis</label>
                      <textarea
                        rows={2}
                        value={gapAnalysis}
                        onChange={(e) => setGapAnalysis(e.target.value)}
                        placeholder="Identified gaps and non-conformities against regulation..."
                      />
                    </div>
                  )}

                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Finding Reference (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g. FND-2026-000001"
                        value={findingRef}
                        onChange={(e) => setFindingRef(e.target.value)}
                      />
                    </div>
                    <div className="form-field">
                      <label>CAPA Reference (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g. CAPA-2026-000001"
                        value={capaRef}
                        onChange={(e) => setCapaRef(e.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px' }}>
                    <button type="submit" className="btn-primary">
                      Save & Publish Assessment
                    </button>
                  </div>
                </form>
              </div>

              {/* Assessment History Log */}
              <div>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Assessment History (Audit Trail)
                </h4>
                {requirement.latest_assessment ? (
                  <div style={{ border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', padding: '16px', background: 'var(--card-bg, #fff)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#dbeafe', color: '#1e40af' }}>
                        {requirement.latest_assessment.status_assigned}
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)' }}>
                        {requirement.latest_assessment.assessment_date.slice(0, 10)} — {requirement.latest_assessment.assessor_name} ({requirement.latest_assessment.assessor_role})
                      </span>
                    </div>
                    {requirement.latest_assessment.why_compliant && (
                      <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary, #334155)' }}>
                        <strong>Justification:</strong> {requirement.latest_assessment.why_compliant}
                      </p>
                    )}
                    {requirement.latest_assessment.gap_analysis && (
                      <p style={{ margin: 0, fontSize: '13px', color: '#991b1b', fontWeight: 500 }}>
                        <strong>Gap Analysis:</strong> {requirement.latest_assessment.gap_analysis}
                      </p>
                    )}
                    {requirement.latest_assessment.finding_reference && (
                      <div style={{ fontSize: '12px', color: '#2563eb', fontFamily: 'monospace' }}>
                        Linked Finding: {requirement.latest_assessment.finding_reference}
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ fontSize: '13px', color: 'var(--text-muted, #64748b)', fontStyle: 'italic' }}>No assessment recorded yet.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'MAPPINGS' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Add mapping form */}
              <div style={{ background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', padding: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>
                  Map Operational Control / Procedure
                </h4>
                <form onSubmit={handleMappingSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr)) 120px', gap: '12px' }}>
                  <div className="form-field">
                    <label>Mapping Type</label>
                    <select
                      value={mapType}
                      onChange={(e) => setMapType(e.target.value as any)}
                    >
                      <option value="SOP_DOCUMENT">SOP / Manual (Document)</option>
                      <option value="RISK">Risk / Barrier Control</option>
                      <option value="AUDIT">Audit Item</option>
                      <option value="FINDING">Quality Finding</option>
                      <option value="CAPA">CAPA Action</option>
                      <option value="TRAINING">Training Syllabus</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Reference ID</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. OMM-SEC-01"
                      value={targetId}
                      onChange={(e) => setTargetId(e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label>Title</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Flight Ops Manual Section 1"
                      value={targetTitle}
                      onChange={(e) => setTargetTitle(e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      type="submit"
                      className="btn-primary"
                      style={{ width: '100%' }}
                    >
                      + Add Link
                    </button>
                  </div>
                </form>
              </div>

              {/* Mappings Table */}
              <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                  <thead style={{ background: 'var(--bg-subtle, #f8fafc)', borderBottom: '1px solid var(--border-color, #e2e8f0)', fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-muted, #64748b)' }}>
                    <tr>
                      <th style={{ padding: '10px 14px' }}>Type</th>
                      <th style={{ padding: '10px 14px' }}>Reference ID</th>
                      <th style={{ padding: '10px 14px' }}>Title</th>
                      <th style={{ padding: '10px 14px', textAlign: 'center' }}>Control Strength</th>
                      <th style={{ padding: '10px 14px', textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody style={{ background: 'var(--card-bg, #fff)' }}>
                    {(!requirement.mappings || requirement.mappings.length === 0) ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)' }}>
                          No operational controls or procedures mapped yet.
                        </td>
                      </tr>
                    ) : (
                      requirement.mappings.map((m) => (
                        <tr key={m.id} style={{ borderBottom: '1px solid var(--border-color, #f1f5f9)' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1e40af' }}>{m.mapping_type}</td>
                          <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: 'var(--text-secondary, #334155)' }}>{m.target_reference_id}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--text-primary, #0f172a)' }}>{m.target_title}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#ecfdf5', color: '#065f46' }}>{m.control_strength}</span>
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                            <button
                              type="button"
                              onClick={() => onRemoveMapping(m.id)}
                              className="btn-secondary"
                              style={{ padding: '3px 8px', fontSize: '11px', color: '#ef4444' }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'SOURCES' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Form to link canonical requirement */}
              <div style={{ background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', padding: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>
                  Map with Authority Framework Article
                </h4>
                <form onSubmit={handleSourceLinkSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr)) 140px', gap: '12px' }}>
                  <div className="form-field">
                    <label>Canonical Article ID</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. REQ-ORO-GEN-200-A1"
                      value={canonicalId}
                      onChange={(e) => setCanonicalId(e.target.value)}
                    />
                  </div>
                  <div className="form-field">
                    <label>Mapping Notes</label>
                    <input
                      type="text"
                      placeholder="e.g. Direct Part-ORO alignment"
                      value={sourceNotes}
                      onChange={(e) => setSourceNotes(e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      type="submit"
                      className="btn-primary"
                      style={{ width: '100%' }}
                    >
                      + Save Link
                    </button>
                  </div>
                </form>
              </div>

              {/* Source links list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {requirement.source_links?.map((link) => (
                  <div key={link.id} style={{ border: '1px solid var(--border-color, #e2e8f0)', padding: '12px 16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--card-bg, #fff)' }}>
                    <div>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#2563eb' }}>
                        {link.canonical_requirement_code || link.canonical_requirement_id}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--text-primary, #0f172a)', marginTop: '2px' }}>{link.canonical_requirement_title}</div>
                      {link.notes && <div style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)', fontStyle: 'italic', marginTop: '2px' }}>{link.notes}</div>}
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#f1f5f9', color: '#475569' }}>
                      {link.link_type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
